import {
  forwardRef,
  ReactElement,
  Ref,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  Slide,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { useNotification } from "../../context/notification-provider";
import {
  bulkUpdateClientScoringRule,
  getClientIdsIgnoringRule,
} from "./service";
import { Client, ClientScoringRule } from "./types";
import {
  filterClientsByName,
  getChangedClientStates,
  getInitiallyEnabledClientIds,
} from "./bulk-edit-utils";

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>,
) {
  // eslint-disable-next-line react/jsx-props-no-spreading -- MUI transitions must forward every prop to Slide
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface BulkEditClientScoringRuleDialogProps {
  open: boolean;
  clientScoringRule: ClientScoringRule | null;
  clients: Client[];
  onClose: (saved: boolean) => void;
}

const BulkEditClientScoringRuleDialog = ({
  open,
  clientScoringRule,
  clients,
  onClose,
}: BulkEditClientScoringRuleDialogProps) => {
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [initiallyEnabled, setInitiallyEnabled] = useState<Set<number>>(
    new Set(),
  );
  const [enabled, setEnabled] = useState<Set<number>>(new Set());

  const ruleId = clientScoringRule?.scoringRule.id;

  useEffect(() => {
    if (!open || ruleId === undefined) return undefined;

    // Ignore a response that arrives after the dialog has closed or switched rule
    let isCurrent = true;
    setIsLoading(true);
    setLoadError(null);

    getClientIdsIgnoringRule(ruleId)
      .then((ignoredClientIds) => {
        if (!isCurrent) return;
        const initial = getInitiallyEnabledClientIds(clients, ignoredClientIds);
        setInitiallyEnabled(initial);
        setEnabled(new Set(initial));
      })
      .catch((err: Error) => {
        if (isCurrent) setLoadError(err.message);
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [open, ruleId, clients]);

  const visibleClients = useMemo(
    () => filterClientsByName(clients, search),
    [clients, search],
  );
  const changes = useMemo(
    () => getChangedClientStates(clients, initiallyEnabled, enabled),
    [clients, initiallyEnabled, enabled],
  );

  const visibleEnabledCount = visibleClients.filter((client) =>
    enabled.has(client.clientInformation.id),
  ).length;
  const allVisibleEnabled =
    visibleClients.length > 0 && visibleEnabledCount === visibleClients.length;
  const someVisibleEnabled = visibleEnabledCount > 0 && !allVisibleEnabled;

  const setVisibleClients = (isEnabled: boolean) => {
    setEnabled((current) => {
      const next = new Set(current);
      visibleClients.forEach(({ clientInformation: { id } }) => {
        if (isEnabled) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const toggleClient = (clientId: number) => {
    setEnabled((current) => {
      const next = new Set(current);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (ruleId === undefined || changes.length === 0) return;

    setIsSubmitting(true);
    try {
      await bulkUpdateClientScoringRule(ruleId, changes);
      showNotification({
        message: `Scoring rule updated for ${changes.length} client${
          changes.length === 1 ? "" : "s"
        }.`,
        severity: "success",
        duration: 3000,
      });
      onClose(true);
    } catch (err) {
      showNotification({
        message: `Failed to update clients for scoring rule. Reason: ${
          (err as Error).message
        }`,
        severity: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!clientScoringRule) return null;

  const renderClientList = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress aria-label="Loading clients" />
        </Box>
      );
    }

    if (loadError) {
      return (
        <Alert severity="error">
          Failed to load clients for this rule: {loadError}
        </Alert>
      );
    }

    if (visibleClients.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No clients match your search.
        </Typography>
      );
    }

    return (
      <List
        dense
        aria-label="Clients"
        sx={{
          maxHeight: 320,
          overflowY: "auto",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        {visibleClients.map(({ clientInformation: { id, name } }) => (
          <ListItem key={id} disablePadding sx={{ px: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={enabled.has(id)}
                  onChange={() => toggleClient(id)}
                />
              }
              label={name}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  const isReady = !isLoading && !loadError;

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="sm"
      fullWidth
      slots={{ transition: Transition }}
    >
      <DialogTitle sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}>
        Manage Clients for Scoring Rule
      </DialogTitle>

      <DialogContent sx={{ pb: 0 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Rule Type:</strong> {clientScoringRule.scoringRule.ruleType}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Description:</strong>{" "}
            {clientScoringRule.scoringRule.description}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Score:</strong> {clientScoringRule.scoringRule.score}
          </Typography>
        </Box>

        <TextField
          label="Search clients"
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={!isReady}
          sx={{ mb: 1 }}
        />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={allVisibleEnabled}
                indeterminate={someVisibleEnabled}
                onChange={(e) => setVisibleClients(e.target.checked)}
                disabled={!isReady || visibleClients.length === 0}
              />
            }
            label="Select all"
          />
          <Typography variant="body2" color="text.secondary">
            {enabled.size} of {clients.length} enabled
          </Typography>
        </Stack>

        {renderClientList()}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isReady || isSubmitting || changes.length === 0}
        >
          Save
          {changes.length > 0 ? ` (${changes.length})` : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkEditClientScoringRuleDialog;
